import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";
import type { LinkedinSenderStates } from "./filterLinkedinSenders.util";
import type { Draft } from "./inboxDraft.types";

/**
 * What the composer shows while the active draft waits for the sender's pending invitation to be
 * accepted (PREC-4216, mockup A2): when the invitation stops waiting, and the colleagues who are
 * connected to the lead and could send the message now instead.
 */
export interface PendingInvitationView {
	senderUserId: number;
	/** The invitation's deadline (ISO); a message parked behind it is dropped then. */
	deadline: string | null;
	/** LinkedIn senders of the company who can message the lead now (connected, or a chat). */
	colleagueOptions: SenderRecipientOption[];
}

/**
 * A LinkedIn message (not an invitation) from a sender whose own invitation to the lead is pending
 * and who has no chat with them: sent now it can only fail, so it waits for the acceptance. The
 * web twin of the API's `planInvitationParking`, which parks exactly these messages.
 */
export const draftWaitsForAcceptance = (
	draft: Draft | undefined,
	pendingInvitations: Map<number, string | null>
): boolean =>
	!!draft &&
	draft.from?.type === "linkedin" &&
	!draft.isInviteNote &&
	draft.from.userId != null &&
	pendingInvitations.has(draft.from.userId);

/**
 * Whether an AI sequence item becomes a connection request in the composer: an invitation note does,
 * unless the draft's sender already has an invitation pending to the lead — a second one cannot be
 * sent, so the note goes out as a message once the first is accepted (PREC-4216). The one decision
 * every builder of drafts from AI items takes (`buildFilteredAiDrafts`, `buildReinjectDrafts`,
 * `rebuildDraftsForChannelSwitch`).
 */
export const aiItemIsInvitation = (
	item: { isInviteNote?: boolean },
	senderUserId: number | undefined,
	pendingInvitations: Map<number, string | null> | undefined
): boolean =>
	!!item.isInviteNote && !(senderUserId != null && pendingInvitations?.has(senderUserId));

/**
 * Whether a send request asks the API to park its LinkedIn messages behind the pending invitation
 * (`waitForInviteAcceptance`): any draft that waits does. The API applies the flag per sender, so
 * a draft from a connected colleague in the same request is scheduled as usual.
 */
export const requestWaitsForAcceptance = (
	drafts: Draft[],
	pendingInvitations: Map<number, string | null>
): boolean => drafts.some((draft) => draftWaitsForAcceptance(draft, pendingInvitations));

/** The notice for the active draft, or null when it does not wait for an acceptance. */
export const resolvePendingInvitationView = (
	draft: Draft | undefined,
	senderStates: LinkedinSenderStates,
	senderOptions: SenderRecipientOption[]
): PendingInvitationView | null => {
	if (!draft || !draftWaitsForAcceptance(draft, senderStates.pendingInvitations)) {
		return null;
	}

	const senderUserId = draft.from.userId;

	return {
		senderUserId,
		deadline: senderStates.pendingInvitations.get(senderUserId) ?? null,
		colleagueOptions: senderOptions.filter(
			(option) =>
				option.type === "linkedin" &&
				!option.disabled &&
				option.userId != null &&
				option.userId !== senderUserId &&
				senderStates.connectedUserIds.has(option.userId)
		),
	};
};
